import { CXProgram } from "./cx-program";
const CXFAMILY_CXID_SEPARATOR = '/';
/**
 * A representation of a CXFamily + CXId pari (see https://w.amazon.com/bin/view/INTech/CXFamilyCXId/).
 *
 * Valid characters in a CXFamily or CXId include
 * English ASCII letters (upper and lower case), dot (.), and dash (-).
 */
export class CXTuple {
    constructor(cxFamily, cxId, cxProgram = CXProgram.EMPTY) {
        this._cxFamily = CXTuple.validateString('cxFamily', cxFamily);
        this._cxId = CXTuple.validateString('cxId', cxId);
        this._cxProgram = cxProgram;
    }
    get cxFamily() {
        return this._cxFamily;
    }
    get cxId() {
        return this._cxId;
    }
    get cxProgram() {
        return this._cxProgram;
    }
    /**
     * Create a CXTuple instance with given cxFamily and cxId.
     */
    static of(cxFamily, cxId, cxProgram) {
        return new CXTuple(cxFamily, cxId, cxProgram);
    }
    /**
     * Create a CXTuple instance with given qualified string.
     * @param fullyQualifiedCXTuple a string of the form "cxFamily/cxId" or "cxFamily/cxId/[exports]"
     */
    static fromString(fullyQualifiedCXTuple) {
        const parts = fullyQualifiedCXTuple.split(CXFAMILY_CXID_SEPARATOR);
        if (parts.length != 2 && parts.length != 3) {
            throw new Error(`Invalid fully qualified CXTuple: ${fullyQualifiedCXTuple}`);
        }
        const cxFamily = parts[0];
        const cxId = parts[1];
        if (parts.length == 3) {
            const cxProgram = CXProgram.fromString(parts[2]);
            return new CXTuple(cxFamily, cxId, cxProgram);
        }
        return new CXTuple(cxFamily, cxId);
    }
    /**
     * Convert the CXTuple to String in the form of "cxFamily/cxId".
     */
    toString() {
        if (!this._cxProgram.isEmpty()) {
            return this._cxFamily + CXFAMILY_CXID_SEPARATOR + this._cxId + CXFAMILY_CXID_SEPARATOR + this._cxProgram.toString();
        }
        return this._cxFamily + CXFAMILY_CXID_SEPARATOR + this._cxId;
    }
    /**
     * Verify if two cxTuple instances are equals.
     *
     * Two instances are equal only when the cxFamily/cxId are identical.
     */
    equals(o) {
        return this._cxFamily === o._cxFamily && this._cxId === this._cxId && this._cxProgram === o._cxProgram;
    }
    static validateString(fieldName, value) {
        if (!value) {
            throw new Error(`${fieldName} must not be empty`);
        }
        for (let i = 0; i < value.length; i++) {
            const c = value.charAt(i);
            // Valid characters are alphanumeric, dot (e.g. "amazon.com"), and dash (e.g. "prime-video").
            // We may allow more characters later, but this is the minimum set required to support existing values.
            if (!(c === '.' || c === '-' || (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9'))) {
                throw new Error(`Invalid character ${c} in ${fieldName}: ${value}`);
            }
        }
        return value;
    }
}
CXTuple.UNKNOWN = new CXTuple('unknown', 'unknown');
//# sourceMappingURL=data:application/json;base64,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